import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import BillingPageFrame from "./BillingPageFrame";
import { listTransactions } from "./billingApi";
import {
    formatCreditCount,
    formatRupeesFromPaisa,
    formatWhen,
    serviceLabel,
} from "./billingFormat";
import { useBilling } from "../../context/BillingContext";

const SERVICE_FILTERS = [
    { value: "Email", label: "Email" },
    { value: "Whatsapp Business", label: "Whatsapp Business" },
    { value: "Personal WhatsApp", label: "Personal WhatsApp" },
];

const TYPE_FILTERS = [
    { value: "", label: "All" },
    { value: "CREDIT", label: "Credit" },
    { value: "DEBIT", label: "Used" },
];

const EMPTY_FILTERS = { service: "", transaction_type: "", per_page: 20 };

const userName = (row) => row.name || row.user_name || row.user?.name || "";
const userEmail = (row) => row.email || row.user?.email || "";

const CreditLedgerPage = () => {
    const { isCreditBased, loading: billingLoading } = useBilling();
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const appliedRef = useRef(EMPTY_FILTERS);
    const [showFilters, setShowFilters] = useState(false);
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalCredit, setTotalCredit] = useState(0);
    const [totalDebit, setTotalDebit] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async (nextPage = 1) => {
        if (!isCreditBased) return;
        const source = appliedRef.current;
        setLoading(true);
        setError("");
        try {
            const params = { page: nextPage, per_page: source.per_page || 20 };
            if (source.service) params.service = source.service;
            if (source.transaction_type) params.transaction_type = source.transaction_type;
            const result = await listTransactions(params);
            setRows(result.rows);
            setLastPage(result.lastPage);
            setTotal(result.total);
            setTotalCredit(Number(result.totalCredit) || 0);
            setTotalDebit(Number(result.totalDebit) || 0);
        } catch (err) {
            setRows([]);
            setTotal(0);
            setTotalCredit(0);
            setTotalDebit(0);
            setError(err?.status === 403
                ? "Only the account admin can view the ledger."
                : (err?.message || "Could not load the ledger"));
        } finally {
            setLoading(false);
        }
    }, [isCreditBased]);

    useEffect(() => {
        if (billingLoading) return;
        if (!isCreditBased) {
            setLoading(false);
            return;
        }
        load(page);
    }, [billingLoading, isCreditBased, page, load]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        appliedRef.current = filters;
        setApplied(filters);
        if (page === 1) load(1);
        else setPage(1);
    };

    const handleReset = () => {
        appliedRef.current = EMPTY_FILTERS;
        setFilters(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
        if (page === 1) load(1);
        else setPage(1);
    };

    const filtersActive = Boolean(applied.service || applied.transaction_type);

    return (
        <BillingPageFrame
            title="Ledger"
            subtitle="Who used credits, which service, how many, and when."
            maxWidth="100%"
            headerAction={(
                <Tooltip title={showFilters ? "Hide filters" : "Filters"}>
                    <IconButton
                        onClick={() => setShowFilters((open) => !open)}
                        aria-label="Filters"
                        color={showFilters ? "primary" : "default"}
                    >
                        <FilterListIcon />
                    </IconButton>
                </Tooltip>
            )}
        >
            {!billingLoading && !isCreditBased ? (
                <Alert severity="info">Ledger is available on credit accounts. Your balance is under Credits Information.</Alert>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
                        {[
                            { label: "On this view", value: String(rows.length), hint: total ? `${total} matching entries` : "Loaded entries" },
                            { label: "Credits added", value: formatRupeesFromPaisa(totalCredit), hint: formatCreditCount(totalCredit), color: "#047857" },
                            { label: "Credits used", value: formatRupeesFromPaisa(totalDebit), hint: formatCreditCount(totalDebit), color: "#b91c1c" },
                        ].map((card) => (
                            <Box key={card.label} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, px: 2, py: 1.5, bgcolor: "#fff" }}>
                                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: card.color || "#111827", letterSpacing: "-0.02em" }}>
                                    {card.value}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">{card.hint}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {showFilters && (
                        <Paper sx={{ p: 2 }}>
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Service</InputLabel>
                                        <Select
                                            value={filters.service}
                                            label="Service"
                                            onChange={(e) => handleFilterChange("service", e.target.value)}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            {SERVICE_FILTERS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={filters.transaction_type}
                                            label="Type"
                                            onChange={(e) => handleFilterChange("transaction_type", e.target.value)}
                                        >
                                            {TYPE_FILTERS.map((option) => (
                                                <MenuItem key={option.label} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Per Page</InputLabel>
                                        <Select
                                            value={filters.per_page}
                                            label="Per Page"
                                            onChange={(e) => handleFilterChange("per_page", Number(e.target.value))}
                                        >
                                            {[20, 50, 100].map((size) => (
                                                <MenuItem key={size} value={size}>{size}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sx={{ display: "flex", gap: 2 }}>
                                    <Button variant="contained" onClick={handleSearch}>Search</Button>
                                    <Button variant="outlined" onClick={handleReset}>Reset</Button>
                                    <Tooltip title="Refresh">
                                        <Button
                                            variant="contained"
                                            onClick={() => load(page)}
                                            startIcon={<RefreshIcon />}
                                        >
                                            Refresh
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {error && (
                        <Alert
                            severity="error"
                            action={<Button color="inherit" size="small" onClick={() => load(page)}>Retry</Button>}
                        >
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ position: "relative", border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
                        {loading && <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0 }} />}
                        {loading && rows.length === 0 ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress size={28} /></Box>
                        ) : rows.length === 0 ? (
                            <Box sx={{ px: 2, py: 5, textAlign: "center" }}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No ledger entries</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {filtersActive
                                        ? "Nothing matches these filters."
                                        : "Credits you add or spend will show up here."}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small" sx={{ width: "100%" }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                            {["Datetime", "Service", "Credits", "User"].map((heading) => (
                                                <TableCell key={heading} sx={{ fontWeight: 700, color: "#475569" }}>{heading}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row, index) => {
                                            const name = userName(row);
                                            const email = userEmail(row);
                                            const added = row.transaction_type === "CREDIT";
                                            const credits = new Intl.NumberFormat("en-IN").format(Math.trunc(Number(row.credit_spent) || 0));
                                            return (
                                                <TableRow key={row.id ?? `${page}-${index}`} hover>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatWhen(row.created_at)}</TableCell>
                                                    <TableCell>{serviceLabel(row.service)}</TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700, color: added ? "#047857" : "#b91c1c" }}>
                                                        {added ? "+" : "−"} {credits}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {name || email || "—"}
                                                        </Typography>
                                                        {name && email && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                                {email}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="body2" color="text.secondary">
                            {`Page ${page} of ${lastPage}`}
                        </Typography>
                        {lastPage > 1 && (
                            <Pagination
                                count={lastPage}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                                shape="rounded"
                            />
                        )}
                    </Box>
                </Box>
            )}
        </BillingPageFrame>
    );
};

export default CreditLedgerPage;
